// services/predictiveMaintenanceService.js
// Enhanced Frontend service module for Predictive Maintenance API calls
// Backward compatible with original API structure + advanced features

// Import shared API helpers
import { authenticatedFetch } from './api';

// ============================================================================
// DEBUG UTILITY
// ============================================================================

class PredictiveServiceserviceDebugger {
  constructor() {
    this.logs = [];
    this.maxLogs = 500;
    this.performanceMarks = new Map();
    this.requestCount = 0;
    this.cacheHits = 0;
    this.cacheMisses = 0;
    this.retryCount = 0;
    this.errorCount = 0;
  }

  log(level, action, data = {}) {
    const logEntry = {
      timestamp: new Date().toISOString(),
      level,
      action,
      service: 'PredictiveMaintenanceService',
      ...data
    };

    this.logs.push(logEntry);
    if (this.logs.length > this.maxLogs) {
      this.logs.shift();
    }

    const colors = {
      INFO: '#2196F3',
      SUCCESS: '#4CAF50',
      WARNING: '#FF9800',
      ERROR: '#F44336',
      DEBUG: '#9C27B0',
      PERFORMANCE: '#00BCD4'
    };

    console.log(
      `%c[${level}] PredictiveMaintenanceService: ${action}`,
      `color: ${colors[level]}; font-weight: bold;`,
      data
    );

    // Store in sessionStorage
    try {
      const storedLogs = JSON.parse(sessionStorage.getItem('predictive_service_logs') || '[]');
      storedLogs.push(logEntry);
      if (storedLogs.length > this.maxLogs) storedLogs.shift();
      sessionStorage.setItem('predictive_service_logs', JSON.stringify(storedLogs));
    } catch (e) {
      console.warn('Failed to store logs:', e);
    }
  }

  startPerformance(marker) {
    this.performanceMarks.set(marker, performance.now());
  }

  endPerformance(marker, action) {
    const start = this.performanceMarks.get(marker);
    if (start) {
      const duration = performance.now() - start;
      this.log('PERFORMANCE', action, {
        duration: `${duration.toFixed(2)}ms`,
        marker
      });
      this.performanceMarks.delete(marker);
      return duration;
    }
    return null;
  }

  incrementRequest() { this.requestCount++; }
  incrementCacheHit() { this.cacheHits++; }
  incrementCacheMiss() { this.cacheMisses++; }
  incrementRetry() { this.retryCount++; }
  incrementError() { this.errorCount++; }

  getStats() {
    return {
      totalRequests: this.requestCount,
      cacheHits: this.cacheHits,
      cacheMisses: this.cacheMisses,
      cacheHitRate: this.requestCount > 0 
        ? (this.cacheHits / this.requestCount * 100).toFixed(2) + '%'
        : '0%',
      retries: this.retryCount,
      errors: this.errorCount,
      totalLogs: this.logs.length
    };
  }

  exportLogs() {
    const dataStr = JSON.stringify(this.logs, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `predictive-service-debug-${Date.now()}.json`;
    link.click();
    URL.revokeObjectURL(url);
  }

  clearLogs() {
    this.logs = [];
    sessionStorage.removeItem('predictive_service_logs');
    this.log('INFO', 'logs_cleared');
  }
}

const serviceDebugger = new PredictiveServiceserviceDebugger();

// ============================================================================
// CACHE UTILITY
// ============================================================================

class RequestCache {
  constructor(ttl = 300000) { // 5 minutes default
    this.cache = new Map();
    this.ttl = ttl;
  }

  get(key) {
    const cached = this.cache.get(key);
    if (!cached) return null;

    const now = Date.now();
    if (now - cached.timestamp > this.ttl) {
      this.cache.delete(key);
      return null;
    }

    return cached.data;
  }

  set(key, data) {
    this.cache.set(key, {
      data,
      timestamp: Date.now()
    });
  }

  clear() {
    this.cache.clear();
  }

  size() {
    return this.cache.size;
  }

  getCacheStats() {
    const now = Date.now();
    let validEntries = 0;
    let totalSize = 0;

    this.cache.forEach((value, key) => {
      if (now - value.timestamp <= this.ttl) {
        validEntries++;
        totalSize += JSON.stringify(value.data).length;
      }
    });

    return {
      entries: validEntries,
      size: `${(totalSize / 1024).toFixed(2)} KB`,
      ttl: `${this.ttl / 1000}s`
    };
  }
}

const cache = new RequestCache();

// ============================================================================
// RETRY UTILITY
// ============================================================================

const retryWithBackoff = async (fn, maxRetries = 3, baseDelay = 1000) => {
  for (let attempt = 1; attempt <= maxRetries; attempt++) {
    try {
      return await fn();
    } catch (error) {
      if (attempt === maxRetries) {
        throw error;
      }

      const delay = baseDelay * Math.pow(2, attempt - 1);
      serviceDebugger.log('WARNING', 'retry_attempt', {
        attempt,
        maxRetries,
        delay: `${delay}ms`,
        error: error.message
      });
      serviceDebugger.incrementRetry();

      await new Promise(resolve => setTimeout(resolve, delay));
    }
  }
};

// ============================================================================
// REQUEST QUEUE (for concurrency control)
// ============================================================================

class RequestQueue {
  constructor(maxConcurrent = 5) {
    this.queue = [];
    this.running = 0;
    this.maxConcurrent = maxConcurrent;
  }

  async add(fn) {
    return new Promise((resolve, reject) => {
      this.queue.push({ fn, resolve, reject });
      this.process();
    });
  }

  async process() {
    if (this.running >= this.maxConcurrent || this.queue.length === 0) {
      return;
    }

    this.running++;
    const { fn, resolve, reject } = this.queue.shift();

    try {
      const result = await fn();
      resolve(result);
    } catch (error) {
      reject(error);
    } finally {
      this.running--;
      this.process();
    }
  }

  getStats() {
    return {
      queueLength: this.queue.length,
      running: this.running,
      maxConcurrent: this.maxConcurrent
    };
  }
}

const requestQueue = new RequestQueue();

// ============================================================================
// ENHANCED API WRAPPER
// ============================================================================

const enhancedFetch = async (endpoint, options = {}, enableCache = true, enableRetry = true) => {
  const cacheKey = `${endpoint}-${JSON.stringify(options)}`;
  
  serviceDebugger.incrementRequest();
  serviceDebugger.log('INFO', 'request_started', { endpoint, method: options.method || 'GET' });
  serviceDebugger.startPerformance(cacheKey);

  // Check cache for GET requests
  if ((!options.method || options.method === 'GET') && enableCache) {
    const cached = cache.get(cacheKey);
    if (cached) {
      serviceDebugger.incrementCacheHit();
      serviceDebugger.log('INFO', 'cache_hit', { endpoint });
      serviceDebugger.endPerformance(cacheKey, 'request_completed_from_cache');
      return cached;
    }
    serviceDebugger.incrementCacheMiss();
  }

  // Execute request with retry logic
  const executeRequest = async () => {
    try {
      const result = await authenticatedFetch(endpoint, options);
      
      // Cache GET requests
      if ((!options.method || options.method === 'GET') && enableCache) {
        cache.set(cacheKey, result);
      }

      const duration = serviceDebugger.endPerformance(cacheKey, 'request_completed');
      serviceDebugger.log('SUCCESS', 'request_success', { 
        endpoint, 
        duration: `${duration?.toFixed(2)}ms`
      });

      return result;
    } catch (error) {
      serviceDebugger.incrementError();
      serviceDebugger.endPerformance(cacheKey, 'request_failed');
      serviceDebugger.log('ERROR', 'request_failed', {
        endpoint,
        error: error.message,
        stack: error.stack
      });
      throw error;
    }
  };

  // Add to queue and execute with retry
  return requestQueue.add(async () => {
    if (enableRetry) {
      return retryWithBackoff(executeRequest);
    } else {
      return executeRequest();
    }
  });
};

// ============================================================================
// ORIGINAL API FUNCTIONS (Enhanced)
// ============================================================================

/**
 * Triggers the predictive maintenance analysis on the backend.
 * @returns {Promise<object>} A promise resolving to the backend response (e.g., { message }).
 * @throws {Error} If API call fails or auth token is missing.
 */
export const triggerAnalysis = async () => {
  const endpoint = '/predictive/run-analysis';
  console.log(`[Frontend PredictiveMaintenanceService] Triggering analysis via POST ${endpoint}`);
  
  const options = { method: 'POST' };
  
  // Use enhanced fetch with retry but no cache (POST request)
  const result = await enhancedFetch(endpoint, options, false, true);
  
  return result;
};

/**
 * Fetches predictive maintenance alerts/recommendations from the backend.
 * @param {object} [filters={}] - Optional filters for alerts (e.g., { severity, assetId, isResolved }).
 * @returns {Promise<Array>} A promise resolving to an array of alert objects.
 * @throws {Error} If API call fails or auth token is missing, or if data format is unexpected.
 */
export const fetchAlerts = async (filters = {}) => {
  const queryParams = new URLSearchParams(filters);
  const endpoint = `/predictive/alerts${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
  console.log(`[Frontend PredictiveMaintenanceService] Fetching alerts from: ${endpoint}`);
  
  try {
    // Use enhanced fetch with cache and retry
    const result = await enhancedFetch(endpoint, {}, true, true);

    // Ensure the result is an array (maintain original validation logic)
    const alerts = Array.isArray(result) ? result : (result?.data || []);

    if (!Array.isArray(alerts)) {
      console.error("[Frontend PredictiveMaintenanceService] fetchAlerts received non-array data:", alerts);
      throw new Error("Received invalid data format for predictive maintenance alerts from server.");
    }

    console.log(`[Frontend PredictiveMaintenanceService] Fetched ${alerts.length} alerts.`);
    return alerts;

  } catch (error) {
    console.error("[Frontend PredictiveMaintenanceService] Error fetching alerts:", error);
    throw error;
  }
};

/**
 * Marks a predictive maintenance alert as resolved on the backend.
 * @param {number} alertId - The ID of the alert to mark as resolved.
 * @returns {Promise<object>} A promise resolving to the backend response (e.g., { message, alert }).
 * @throws {Error} If API call fails or auth token is missing.
 */
export const markAlertAsResolved = async (alertId) => {
  if (!alertId) {
    console.warn("[Frontend PredictiveMaintenanceService] markAlertAsResolved called without alertId.");
    throw new Error("Alert ID is required to mark as resolved.");
  }
  
  const endpoint = `/predictive/alerts/${alertId}/resolve`;
  console.log(`[Frontend PredictiveMaintenanceService] Marking alert ${alertId} as resolved via PATCH ${endpoint}`);
  
  const options = { method: 'PATCH' };

  // Use enhanced fetch with retry but no cache (PATCH request)
  const result = await enhancedFetch(endpoint, options, false, true);
  
  // Invalidate alerts cache since data changed
  cache.clear();
  serviceDebugger.log('INFO', 'cache_invalidated', { reason: 'alert_resolved' });
  
  return result;
};

/**
 * Fetches the calculated health scores for all assets.
 * @returns {Promise<object>} The API response with health scores.
 * @throws {Error} If API call fails.
 */
export const getHealthScores = async () => {
  try {
    // Use enhanced fetch with cache and retry
    const response = await enhancedFetch('/predictive/health-scores', {}, true, true);
    return response;
  } catch (error) {
    console.error('Error fetching asset health scores:', error);
    throw error;
  }
};

/**
 * Submits user feedback on a predictive alert's accuracy.
 * @param {number} alertId - The ID of the alert.
 * @param {boolean} wasAccurate - Whether the user confirmed or denied the alert.
 * @returns {Promise<object>} The API response.
 * @throws {Error} If API call fails or alertId is missing.
 */
export const submitAlertFeedback = async (alertId, wasAccurate) => {
  if (!alertId) {
    throw new Error("Alert ID is required to submit feedback.");
  }

  const endpoint = `/predictive/alerts/${alertId}/feedback`;
  const options = {
    method: 'POST',
    body: JSON.stringify({ was_accurate: wasAccurate }),
  };

  // Use enhanced fetch with retry but no cache (POST request)
  const result = await enhancedFetch(endpoint, options, false, true);
  
  // Invalidate alerts cache since feedback might affect data
  cache.clear();
  serviceDebugger.log('INFO', 'cache_invalidated', { reason: 'feedback_submitted' });
  
  return result;
};

// ============================================================================
// ADDITIONAL ENHANCED API FUNCTIONS (Optional - for future use)
// ============================================================================

/**
 * Gets statistics about the predictive maintenance system.
 * @returns {Promise<object>} Statistics object.
 */
export const getStatistics = async () => {
  try {
    const response = await enhancedFetch('/predictive/statistics', {}, true, true);
    return response;
  } catch (error) {
    console.error('Error fetching statistics:', error);
    throw error;
  }
};

/**
 * Gets maintenance recommendations for a specific asset.
 * @param {number} assetId - The asset ID.
 * @returns {Promise<object>} Recommendations object.
 */
export const getAssetRecommendations = async (assetId) => {
  if (!assetId) {
    throw new Error("Asset ID is required.");
  }

  try {
    const response = await enhancedFetch(
      `/predictive/assets/${assetId}/recommendations`, 
      {}, 
      true, 
      true
    );
    return response;
  } catch (error) {
    console.error(`Error fetching recommendations for asset ${assetId}:`, error);
    throw error;
  }
};

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Clears the request cache.
 */
export const clearCache = () => {
  cache.clear();
  serviceDebugger.log('INFO', 'cache_cleared_manually');
};

/**
 * Gets cache statistics.
 * @returns {object} Cache statistics.
 */
export const getCacheStats = () => {
  return cache.getCacheStats();
};

/**
 * Gets request queue statistics.
 * @returns {object} Queue statistics.
 */
export const getQueueStats = () => {
  return requestQueue.getStats();
};

/**
 * Gets service statistics.
 * @returns {object} Service statistics.
 */
export const getServiceStats = () => {
  return serviceDebugger.getStats();
};

/**
 * Exports debug logs to a file.
 */
export const exportDebugLogs = () => {
  serviceDebugger.exportLogs();
};

/**
 * Clears debug logs.
 */
export const clearDebugLogs = () => {
  serviceDebugger.clearLogs();
};

// ============================================================================
// EXPOSE DEBUG UTILITIES GLOBALLY
// ============================================================================

if (typeof window !== 'undefined') {
  window.__PredictiveMaintenanceDebug__ = {
    getCacheStats,
    getQueueStats,
    getServiceStats,
    exportDebugLogs,
    clearCache,
    clearDebugLogs,
    getLogs: () => serviceDebugger.logs
  };

  // Log initialization
  serviceDebugger.log('INFO', 'service_initialized', {
    cacheTTL: `${cache.ttl / 1000}s`,
    maxConcurrent: requestQueue.maxConcurrent,
    maxRetries: 3
  });
}

// ============================================================================
// DEFAULT EXPORT (for convenience)
// ============================================================================

export default {
  triggerAnalysis,
  fetchAlerts,
  markAlertAsResolved,
  getHealthScores,
  submitAlertFeedback,
  getStatistics,
  getAssetRecommendations,
  clearCache,
  getCacheStats,
  getQueueStats,
  getServiceStats,
  exportDebugLogs,
  clearDebugLogs
};